'use client';
import { AppDispatch, RootState } from '@/redux/store';
import { EXIST_LOCAL_STORAGE } from '@/services/constants/app';
import { getCookie, isEmpty } from '@/services/helperFunctions';
import { ISearchFilters } from '@/services/interface/searchForm';
import {
    faArrowRotateRight,
    faEarthAmerica,
    faEllipsisVertical,
    faEquals,
    faFileContract,
    faFloppyDisk,
    faGear,
    faGlobe,
    faIndustry,
    faMoneyCheckDollar,
    faStarOfLife,
    faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import { ChangeResult } from 'multi-range-slider-react';
const RangeSlider = dynamic(() => import('multi-range-slider-react'), { ssr: false });
import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Dropdown, DropdownButton } from 'react-bootstrap';
import InfiniteScroll from 'react-infinite-scroll-component';
import { connect, ConnectedProps } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import * as commonAction from '../../redux/actions/common';
import CreateFilter from './searchFormComponents/CreateFilter';
import DropdownSelect from './searchFormComponents/DropdownSelect';
import RenameFilter from './searchFormComponents/RenameFilter';
import dynamic from 'next/dynamic';

type optionList = { name: string; value: string }[];

type ISearcForm = {
    filterObj: ISearchFilters;
    setFilterObj: React.Dispatch<React.SetStateAction<ISearchFilters>>;
    recordsPerPage: number;
    filterData: Record<string, any>;
    setFilterDataList: React.Dispatch<React.SetStateAction<Record<string, any>>>;
    filterCloseList: Record<string, any>;
    handleGetList: (params: ISearchFilters, isReset?: boolean, isFilter?: boolean) => void;
    is_loading: boolean;
    handleReset: () => void;
    testSlider: any;
    setTestSilder: React.Dispatch<React.SetStateAction<any>>;
    isLoggedIn: boolean;
    noFilterBonds: any;
    getBondsFilterDetails: (body: any) => Promise<any>;
    deleteFilterList: (body: any) => Promise<any>;
    getBondsFilterList: (body: any) => Promise<any>;
};

type SearchFormProps = PropsFromRedux & ISearcForm;

const SearchForm: React.FC<SearchFormProps> = ({
    filterObj,
    setFilterObj,
    handleGetList,
    is_loading,
    handleReset,
    testSlider,
    setTestSilder,
    isLoggedIn,
    getCountry,
    getCurrency,
    countryData,
    currencyData,
    issueTypeData,
    industryData,
    regionData,
    maturityData,
    getRegion,
    getIndustry,
    getSector,
    getIssueType,
    isIssueTypeLoader,
    getBondsFilterDetails,
    getMaturityYears,
    setFilterDataList,
    filterCloseList,
    noFilterBonds,
    recordsPerPage,
    getBondsFilterList,
    filterData,
    deleteFilterList,
    ...rest
}) => {
    console.log('Rendering SearchForm component with filterobj:', filterObj);

    const isLoadingDropValuesRef = useRef(false);
    const handleGetAllCommonData = useCallback(async () => {
        const isCommonDataAvailable = !!countryData.code && !!currencyData.code && !!industryData.code && !!regionData.code;
        if ((isLoadingDropValuesRef && isLoadingDropValuesRef.current) || isCommonDataAvailable) {
            return;
        } else {
            isLoadingDropValuesRef.current = true;
            Promise.all([getCountry(), getCurrency(), getRegion(), getIndustry(), getIssueType()])
                .then(() => {
                    const localStorageData = getCookie(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ);
                    const filterStoreObj = localStorageData ? JSON.parse(localStorageData) : filterObj;
                    setTestSilder({
                        test_from: filterStoreObj?.yield_from,
                        test_to: filterStoreObj?.yield_to,
                    });
                    if (isEmpty(filterStoreObj)) {
                        setFilterObj({
                            ...filterObj,
                        });
                    }
                })
                .catch((e) => console.log('err------->', e));
        }
    }, [
        countryData.code,
        currencyData.code,
        industryData.code,
        regionData.code,
        getCountry,
        getCurrency,
        getRegion,
        getIndustry,
        getIssueType,
        filterObj,
        setFilterObj,
    ]);

    useEffect(() => {
        handleGetAllCommonData();
        fetchData();
    }, []);

    const countryDataToMap: optionList = useMemo(
        () => countryData?.data?.map((item: any) => ({ name: item.country_name, value: item.country_code })),
        [countryData?.data?.length]
    );
    const remainingCurrencyDataToMap: optionList = useMemo(
        () => currencyData?.data?.remaining?.map((item: any) => ({ name: item.currency_name, value: item.currency_code })),
        [currencyData?.data?.remaining?.length]
    );
    const topCurrencyDataToMap: optionList = useMemo(
        () => currencyData?.data?.top_currencies?.map((item: any) => ({ name: item.currency_name, value: item.currency_code })),
        [currencyData?.data?.top_currencies?.length]
    );
    const industryDataToMap: optionList = useMemo(
        () => industryData?.data?.map((item: any) => ({ name: item.industry })),
        [industryData?.data?.length]
    );
    const regionDataToMap: optionList = useMemo(
        () => regionData?.data?.map((item: any) => ({ name: item.region })),
        [regionData?.data?.length]
    );
    const issueTypeDataToMap: optionList = useMemo(() => issueTypeData?.data, [issueTypeData?.data]);

    // const initMaturitySliderRange: maturityDataForSliderType = {
    //     min_maturity_year: new Date().getFullYear(),
    //     max_maturity_year: new Date().getFullYear() + 100,
    //     status_min_maturity_year: new Date().getFullYear(),
    //     status_max_maturity_year: new Date().getFullYear() + 100,
    // };

    // const maturityDataForSlider: maturityDataForSliderType = useMemo(
    //     () =>
    //         maturityData?.data && Array.isArray(maturityData.data) && maturityData.data[0] ? maturityData.data[0] : initMaturitySliderRange,
    //     [maturityData?.data?.length]
    // );

    const currentYear = new Date().getFullYear();
    const min_maturity_year = !filterObj.status ? currentYear : currentYear - 1;
    const max_maturity_year = currentYear + 100;

    const [currentPageList, setCurrentPageList] = useState(1);
    const [savedSearchFiltersData, setSavedSearchFiltersData] = useState<any[]>([]);
    const [filterDataLoading, setFilterDataLoading] = useState(false);

    function handleSelectIssuerName(e: React.ChangeEvent<HTMLSelectElement>) {
        throw new Error('Function not implemented.');
    }

    const handleInputChange = useCallback(
        (e: ChangeEvent<HTMLInputElement | HTMLSelectElement> | { target: { name: string; value: string; type: string } }) => {
            const { name, value, type } = e.target;
            const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
            setFilterObj((prev) => ({ ...prev, [name]: newValue }));
        },
        [setFilterObj]
    );

    const handleTestSlider = useCallback(
        (e: ChangeResult, from: string, to: string) => {
            setTestSilder((pre: Record<string, any>) => {
                return {
                    ...pre,
                    [from]: e.minValue,
                    [to]: e.maxValue,
                };
            });
            setFilterObj((prev) => ({
                ...prev,
                [from]: e.minValue,
                [to]: e.maxValue,
                page_no: 1,
            }));

            if (from !== 'yield_from' || to !== 'yield_to') {
                setFilterDataList((prev) => ({ ...prev, Maturity: e.minValue + ' - ' + e.maxValue }));
            } else {
                setFilterDataList((prev) => ({ ...prev, Yield: e.minValue + '%' + ' - ' + e.maxValue + '%' }));
            }
        },
        [setTestSilder, setFilterObj, setFilterDataList]
    );

    const debouncedHandleSliderTest = useMemo(
        () =>
            debounce((e: ChangeResult, from: string, to: string) => {
                setTestSilder((prev: any) => ({
                    ...prev,
                    [from]: e.minValue,
                    [to]: e.maxValue,
                }));

                if (from === 'maturity_from_year' && to === 'maturity_to_year') {
                    setFilterObj((prevFilterObj) => ({
                        ...prevFilterObj,
                        [from]: e.minValue,
                        [to]: e.maxValue,
                        page_no: 1,
                    }));
                    setFilterDataList((prev: any) => ({ ...prev, Maturity: `${e.minValue} - ${e.maxValue}` }));
                } else {
                    setFilterObj((prevFilterObj) => ({
                        ...prevFilterObj,
                        yield_from: e.minValue,
                        yield_to: e.maxValue,
                        page_no: 1,
                    }));
                    setFilterDataList((prev: any) => ({ ...prev, Yield: `${e.minValue}% - ${e.maxValue}%` }));
                }
            }, 500),
        [setTestSilder, setFilterObj, setFilterDataList]
    );

    // Maturity and Coupon slider input change handler
    const handleTestSliderInput = useCallback(
        (e: ChangeResult, from: string, to: string) => {
            debouncedHandleSliderTest(e, from, to);
        },
        [debouncedHandleSliderTest]
    );

    const handleSliderChange = (e: ChangeResult, from: string, to: string) => {
        //console.log("It is useful too")
        setFilterObj({
            ...filterObj,
            [from]: e.minValue,
            [to]: e.maxValue,
            page_no: 1,
        });
        if (from === 'coupon_from' || to === 'coupon_to') {
            setFilterDataList((prev: any) => ({ ...prev, Coupon: e.minValue + '%' + ' - ' + e.maxValue + '%' }));
        }
    };

    const debouncedHandleSliderInputChange = useMemo(
        () =>
            debounce((e, from, to) => {
                setFilterObj((prevFilterObj) => ({
                    ...prevFilterObj,
                    [from]: e.minValue,
                    [to]: e.maxValue,
                    page_no: 1,
                }));

                if (from === 'coupon_from' || to === 'coupon_to') {
                    setFilterDataList((prev: any) => ({ ...prev, Coupon: `${e.minValue}% - ${e.maxValue}%` }));
                }
            }, 500),
        [setFilterObj, setFilterDataList]
    );

    const hasMounted = useRef<boolean>(false);

    useEffect(() => {
        const { ...rest } = filterCloseList;

        let data = { ...filterObj, ...rest };

        setTestSilder({
            test_from: data.yield_from,
            test_to: data.yield_to,
        });

        if (rest?.Maturity === '') {
            const currentYear = new Date().getFullYear();
            setFilterObj({
                ...filterObj,
                maturity_from_year: currentYear,
                maturity_to_year: currentYear + 100,
            });

            data = {
                ...filterObj,
                maturity_from_year: currentYear,
                maturity_to_year: currentYear + 100,
            };
        } else if (rest?.Yield === '') {
            setFilterObj({
                ...filterObj,
                yield_from: 0,
                yield_to: 30,
            });

            setTestSilder({
                test_from: 0,
                test_to: 30,
            });

            data = {
                ...filterObj,
                yield_from: 0,
                yield_to: 30,
            };
        } else if (rest?.Coupon === '') {
            setFilterObj({
                ...filterObj,
                coupon_from: 0,
                coupon_to: 30,
            });

            data = {
                ...filterObj,
                coupon_from: 0,
                coupon_to: 30,
            };
        } else {
            // handles empty value for issuer_name, issue type, region, country, currency, industry, status, callable

            setFilterObj({
                ...filterObj,
                ...rest,
            });
        }

        if (!hasMounted.current) {
            hasMounted.current = true;
            setFilterDataList(data);
            return; // Skip first render
        }
        handleGetList(data);
    }, [filterCloseList]);

    // Coupon Slider input change handler
    const handleSliderInputChange = useCallback(
        (e: ChangeResult, from: string, to: string) => {
            debouncedHandleSliderInputChange(e, from, to);
        },
        [debouncedHandleSliderInputChange]
    );

    const [isCreatefilterModal, setIsCreatefilterModal] = useState(false);

    const [showSavedFiltersAccordion, setShowSavedFiltersAccordion] = useState(false);

    const [showRename, setShowRename] = useState(false);
    const [filterRename, setFilterRename] = useState({
        filter_name: '',
        search_filter_id: null,
    });

    function fetchMoreData() {
        fetchData();
    }

    const [hasMore, setHasMore] = useState(true);

    const fetchData = useCallback(
        (isForced = false) => {
            if (filterDataLoading) return;

            setFilterDataLoading(true);

            try {
                const AUTHTOKEN = getCookie(EXIST_LOCAL_STORAGE.AUTHTOKEN);
                const UserId = getCookie(EXIST_LOCAL_STORAGE.USER_ID);
                if (AUTHTOKEN && (hasMore || isForced)) {
                    const body = {
                        params: {
                            page_no: isForced ? 1 : currentPageList,
                            per_page: 10, // Suspicious code
                            user_id: UserId,
                        },
                    };
                    setCurrentPageList((prev) => (isForced ? 2 : prev + 1));
                    if (!getBondsFilterList) return;
                    getBondsFilterList(body).then((response: any) => {
                        if (response.page_no === 1) {
                            setSavedSearchFiltersData(response.search_filters);
                        } else {
                            setSavedSearchFiltersData((prev) => [
                                ...prev,
                                ...(Array.isArray(response?.search_filters) ? response.search_filters : []),
                            ]);
                        }
                        setHasMore(response.end_record === response.total_records ? false : true);
                    });
                }
            } catch (error) {
                console.error(error, 'error on getting searchfilter');
            } finally {
                setFilterDataLoading(false);
            }
        },
        [hasMore, currentPageList, getBondsFilterList, setSavedSearchFiltersData]
    );

    const handleUpdateSavedFilterListAfterCreateFilter = useCallback(() => {
        setSavedSearchFiltersData([]);
        // setFilterDataLoading(true)
        fetchData(true);
    }, []);
    // A handle function to rename a saved filter
    const handleRenameSavedFilter = useCallback(() => {
        fetchData(true);
        // fetchData()
    }, []);

    const handleRenameFilter = (filter_name: any, search_filter_id: any) => {
        setFilterRename({
            filter_name: filter_name,
            search_filter_id: search_filter_id,
        });
        setShowRename(true);
    };

    const handleDeleteFilter = useCallback(
        (search_filter_id: any) => {
            const body = {
                params: {
                    search_filter_id: search_filter_id,
                },
            };
            deleteFilterList(body).then((res: any) => {
                const removedSearchFilter = savedSearchFiltersData.filter((item: any) => item.search_filter_id !== search_filter_id);
                setSavedSearchFiltersData(removedSearchFilter);
                fetchData(true);
            });
        },
        [deleteFilterList, savedSearchFiltersData, fetchData]
    );

    const handleSetallDataFilter = useCallback(
        async (searchId: number) => {
            const searchFilterDetailData = await getBondsFilterDetails(searchId);
            if (searchFilterDetailData && searchFilterDetailData?.data?.search_details?.length > 0 && searchId) {
                let filterData = {} as ISearchFilters;
                let testFrom = 0; // Default value for test_from
                let testTo = 30; // Default value for test_to
                (async () => {
                    let obj = {} as Record<string, string>;
                    for (const { html_field_name, html_field_value } of searchFilterDetailData?.data?.search_details) {
                        obj = { ...obj, [html_field_name]: html_field_value };
                        filterData = {
                            ...filterData,
                            [html_field_name]:
                                html_field_name === 'status'
                                    ? html_field_value === 'Active'
                                        ? false
                                        : true
                                    : html_field_name === 'callable'
                                    ? html_field_value === 'N'
                                        ? true
                                        : false
                                    : html_field_value,
                        };
                        if (html_field_name === 'yield_from') {
                            testFrom = parseInt(html_field_value, 10); // Parse the value to an integer
                        }
                        if (html_field_name === 'yield_to') {
                            testTo = parseInt(html_field_value, 10); // Parse the value to an integer
                        }
                    }

                    obj = {
                        issue_type: '',
                        issuer: '',
                        ...obj,
                        Maturity: obj.maturity_from_year + ' - ' + obj.maturity_to_year,
                        Coupon: obj.coupon_from + '%' + ' - ' + obj.coupon_to + '%',
                        Yield: obj.yield_from + '%' + ' - ' + obj.yield_to + '%',
                    };

                    Object.keys(obj).forEach((key) => {
                        (key === 'user_id' ||
                            key === 'description' ||
                            key === 'fulltext' ||
                            key === 'page_no' ||
                            key === 'per_page' ||
                            key === 'sorting_column_sorting_order' ||
                            key === 'coupon_from' ||
                            key === 'coupon_to' ||
                            key === 'yield_from' ||
                            key === 'yield_to' ||
                            key === 'test_from' ||
                            key === 'test_to' ||
                            key === 'yield_to' ||
                            key === 'maturity_from_year' ||
                            key === 'maturity_to_year' ||
                            obj[key] === 'Active' ||
                            obj[key] === '') &&
                            delete obj[key];
                    });

                    setTestSilder(() => ({ test_from: testFrom, test_to: testTo }));
                    handleGetList(filterData, false, false);
                    setFilterDataList(obj); // This line must be below the above handleGetList function call line. Then only search filters apply
                    setFilterObj(filterData);
                    // setStorage(EXIST_LOCAL_STORAGE.BOND_FILTER_OBJ, JSON.stringify(filterData));
                    console.log('search triggered from Search filter');
                })();
            }
        },
        [getBondsFilterDetails, handleGetList, setFilterDataList, setFilterObj, setTestSilder]
    );

    const handleRedirectSearch = useCallback(
        (search_filter_id: any) => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
            handleSetallDataFilter(search_filter_id);
        },
        [handleSetallDataFilter]
    );

    return (
        <div className="mt-2">
            <div className="container d-flex flex-column align-items-center mt-1 rounded-1 p-0">
                <div className="row" style={{ width: '100%' }}>
                    {/* Issuer name */}
                    <div
                        className={`col-lg-11 mx-auto mt-1 d-flex align-items-center justify-content-start border bg-white rounded did-floating-label-content p-1 ${
                            is_loading && 'opacity-75 pe-none'
                        }`}
                    >
                        <span className="input-group-prepend">
                            <div className="input-group-text border-gray border-0 border-end  bg-white px-1">
                                <label htmlFor="issuer">
                                    {filterObj?.issuer_search_type ? (
                                        <FontAwesomeIcon size="xs" icon={filterObj?.issuer_search_type === 'C' ? faStarOfLife : faEquals} />
                                    ) : (
                                        <span />
                                    )}
                                    <select
                                        name="issuer_search_type"
                                        value={filterObj?.issuer_search_type}
                                        style={{ width: '15px', border: 'none', outline: 'none', marginLeft: '3px' }}
                                        onChange={(e) => handleInputChange(e)}
                                    >
                                        <option value=""></option>
                                        <option value="C">Contains</option>
                                        <option value="E">Equals</option>
                                    </select>
                                </label>
                            </div>
                        </span>
                        <input
                            type="text"
                            name="issuer"
                            aria-label="issuer"
                            aria-details={filterObj.issuer}
                            className="border-0 col-8 ps-1 font-size-12 did-floating-input flex-grow-1"
                            value={filterObj?.issuer}
                            onChange={(e) => handleInputChange(e)}
                        />
                        {!!filterObj?.issuer?.length ? (
                            <FontAwesomeIcon
                                icon={faXmark}
                                className="col-1 me-1"
                                onClick={() => handleInputChange({ target: { name: 'issuer', value: '', type: 'text' } })}
                            />
                        ) : (
                            <div className="col-1"></div>
                        )}
                        <label className="did-floating-label mx-2">Issuer name</label>
                    </div>

                    {/* Issue type */}
                    <DropdownSelect
                        key="issue_type"
                        label="Issue type"
                        name="issue_type"
                        is_loading={is_loading}
                        handleInputChange={handleInputChange}
                        optionsDataList={[issueTypeDataToMap]}
                        selectedOption={filterObj.issue_type}
                        icon={faFileContract}
                    />

                    {/* Region */}
                    <DropdownSelect
                        key="region"
                        label="Region"
                        name="region"
                        is_loading={is_loading}
                        handleInputChange={handleInputChange}
                        optionsDataList={[regionDataToMap]}
                        selectedOption={filterObj.region}
                        icon={faGlobe}
                        includeAlloption
                    />

                    {/* Country */}
                    <DropdownSelect
                        key="country"
                        label="Country"
                        name="country"
                        is_loading={is_loading}
                        handleInputChange={handleInputChange}
                        optionsDataList={[countryDataToMap]}
                        selectedOption={filterObj.country}
                        icon={faEarthAmerica}
                        includeAlloption
                    />
                </div>

                <div className="row" style={{ width: '100%' }}>
                    {/* Currency */}
                    <DropdownSelect
                        key="currency"
                        label="Currency"
                        name="currency"
                        is_loading={is_loading}
                        handleInputChange={handleInputChange}
                        optionsDataList={[topCurrencyDataToMap, remainingCurrencyDataToMap]}
                        selectedOption={filterObj.currency}
                        icon={faMoneyCheckDollar}
                        includeAlloption
                    />

                    {/* Industry */}
                    <DropdownSelect
                        key="industry"
                        label="Industry"
                        name="industry"
                        is_loading={is_loading}
                        handleInputChange={handleInputChange}
                        optionsDataList={[industryDataToMap]}
                        selectedOption={filterObj.industry}
                        icon={faIndustry}
                        includeAlloption
                    />

                    {/* Callable/Inactive */}
                    <div className="col-lg-12 col-sm-12 my-lg-1 px-0 px-lg-2">
                        <div className="d-flex d-lg-block">
                            <div className="checkboxes__item my-2 px-1 d-flex align-items-center">
                                <label className="checkbox style-d">
                                    <input
                                        id="callable"
                                        name="callable"
                                        type="checkbox"
                                        disabled={is_loading}
                                        // defaultChecked={true}
                                        onChange={(e) => handleInputChange(e)}
                                        // value={filterObj?.callable}
                                        checked={!!filterObj?.callable}
                                    />
                                    {/* {//console.log('filterObj?.callable', filterObj?.callable)} */}
                                    <div className="checkbox__checkmark"></div>
                                    <div className="checkbox__body">Exclude Callable</div>
                                </label>
                            </div>
                            <div className="checkboxes__item my-2 px-1 d-flex align-items-center">
                                <label className="checkbox style-d">
                                    <input
                                        type="checkbox"
                                        name="status"
                                        disabled={is_loading}
                                        onChange={(e) => handleInputChange(e)}
                                        checked={filterObj?.status}
                                    />
                                    <div className="checkbox__checkmark"></div>
                                    <div className="checkbox__body">Include Inactive</div>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row" style={{ width: '100%' }}>
                    {/* Maturity */}
                    <div className="col-lg-12 col-sm-12 px-0 px-lg-2 py-1 ">
                        <div className="mt-2 mt-lg-0">
                            {/* <div className={`${is_loading ? 'disabled-slider' : ''}`}> */}
                            <label className="form-label px-1 text-xs" style={{ padding: 0, marginBottom: '5px' }}>
                                Maturity{' '}
                                <small>
                                    ({filterObj?.maturity_from_year} - {filterObj?.maturity_to_year}
                                    {max_maturity_year === filterObj?.maturity_to_year ? '+' : ''})
                                </small>
                            </label>
                            <RangeSlider
                                style={{
                                    width: '100%',
                                    border: 'none',
                                    boxShadow: 'none',
                                    padding: '0px 5px',
                                    paddingLeft: '13px',
                                    paddingRight: '10px',
                                }}
                                className={`maturity-slider ${
                                    max_maturity_year === filterObj?.maturity_to_year ? 'thumb-maturity' : 'thumb'
                                } ${is_loading ? 'disabled-slider' : ''}`}
                                label={false}
                                ruler={false}
                                barLeftColor="#808080"
                                barInnerColor="#1E90FF"
                                barRightColor="#808080"
                                thumbLeftColor="#ccc"
                                thumbRightColor="#ccc"
                                // min={
                                //     !filterObj?.status
                                //         ? maturityDataForSlider.status_min_maturity_year
                                //             ? maturityDataForSlider.status_min_maturity_year
                                //             : new Date().getFullYear()
                                //         : maturityDataForSlider.min_maturity_year
                                //         ? maturityDataForSlider.min_maturity_year
                                //         : new Date().getFullYear()
                                // }
                                // max={
                                //     maturityDataForSlider.min_maturity_year
                                //         ? maturityDataForSlider.min_maturity_year + 100
                                //         : new Date().getFullYear() + 100
                                // }
                                min={min_maturity_year}
                                max={max_maturity_year}
                                step={1}
                                disabled={is_loading}
                                minValue={filterObj?.maturity_from_year}
                                maxValue={filterObj?.maturity_to_year}
                                onChange={(e: ChangeResult) => handleTestSlider(e, 'maturity_from_year', 'maturity_to_year')}
                                onInput={(e: ChangeResult) => handleTestSliderInput(e, 'maturity_from_year', 'maturity_to_year')}
                            />
                        </div>
                    </div>

                    {/* Coupon */}
                    <div className="col-lg-12 col-sm-12 px-0 px-lg-2 py-1">
                        <div className="mt-2 mt-lg-0">
                            <label className="form-label px-1 text-xs" style={{ padding: 0, marginBottom: '5px' }}>
                                Coupon %{' '}
                                <small>
                                    ({filterObj?.coupon_from} - {filterObj?.coupon_to}
                                    {filterObj?.coupon_to === 30 ? '+' : ''})
                                </small>
                            </label>
                            <RangeSlider
                                style={{
                                    border: 'none',
                                    boxShadow: 'none',
                                    padding: '0px 5px',
                                    paddingLeft: '13px',
                                    paddingRight: '10px',
                                }}
                                className={`${filterObj?.coupon_to === 30 ? 'thumb-coupon' : 'thumb-coupon1'} ${
                                    is_loading ? 'disabled-slider' : ''
                                }`}
                                label="false"
                                ruler="false"
                                barLeftColor="#808080"
                                barInnerColor="#1E90FF"
                                barRightColor="#808080"
                                thumbLeftColor="#ccc"
                                thumbRightColor="#ccc"
                                min={0}
                                max={30}
                                step={1}
                                minValue={filterObj?.coupon_from}
                                maxValue={filterObj?.coupon_to}
                                // formatLabel={(value) => `${value}%`}
                                onChange={(e: ChangeResult) => handleSliderChange(e, 'coupon_from', 'coupon_to')}
                                onInput={(e: ChangeResult) => handleSliderInputChange(e, 'coupon_from', 'coupon_to')}
                            />
                        </div>
                    </div>

                    {/* Yield */}
                    <div className="col-lg-12 col-sm-12 px-0 px-lg-2 py-1">
                        <div className="mt-2 mt-lg-0">
                            <label className="form-label px-1 text-xs" style={{ padding: 0, marginBottom: '5px' }}>
                                Yield %{' '}
                                <small>
                                    ({filterObj?.yield_from} - {filterObj?.yield_to}
                                    {filterObj?.yield_to === 30 ? '+' : ''})
                                </small>
                            </label>
                            <RangeSlider
                                style={{
                                    // minWidth: "250px",
                                    border: 'none',
                                    boxShadow: 'none',
                                    padding: '0',
                                    paddingLeft: '13px',
                                    paddingRight: '10px',
                                }}
                                className={`${testSlider?.test_to === 30 ? 'thumb-yield' : 'thumb-yield1'} ${
                                    is_loading ? 'disabled-slider' : ''
                                }`}
                                label="false"
                                ruler="false"
                                barLeftColor="#808080"
                                barInnerColor="#1E90FF"
                                barRightColor="#808080"
                                thumbLeftColor="#ccc"
                                thumbRightColor="#ccc"
                                min={0}
                                max={30}
                                step={1}
                                minValue={filterObj?.yield_from}
                                maxValue={filterObj?.yield_to}
                                onChange={(e: ChangeResult) => handleTestSlider(e, 'yield_from', 'yield_to')}
                                onInput={(e: ChangeResult) => handleTestSliderInput(e, 'yield_from', 'yield_to')}
                            />
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-column d-none">
                        <label className="form-label px-1">
                            Yield %
                            <small>
                                ({filterObj?.yield_from} - {filterObj?.yield_to}
                                {filterObj?.yield_to === 30 ? '+' : ''})
                            </small>
                        </label>
                        <RangeSlider
                            style={{
                                border: 'none',
                                boxShadow: 'none',
                                padding: '5px 5px',
                                paddingLeft: '13px',
                                paddingRight: '10px',
                                display: 'none',
                            }}
                            className={`${filterObj?.yield_to === 30 ? 'thumb-yield' : 'thumb-yield1'}`}
                            label="false"
                            ruler="false"
                            barLeftColor="#808080"
                            barInnerColor="#1E90FF"
                            barRightColor="#808080"
                            thumbLeftColor="#ccc"
                            thumbRightColor="#ccc"
                            min={0}
                            max={30}
                            step={1}
                            minValue={filterObj?.yield_from === 0 ? 0 : filterObj?.yield_from}
                            maxValue={filterObj?.yield_to === 30 ? 30 : filterObj?.yield_to}
                            // formatLabel={(value: string) => `${value}%`}
                            onChange={(e: ChangeResult) => handleSliderChange(e, 'yield_from', 'yield_to')}
                        />
                    </div>

                    {/* Reset */}
                    <div className="col-lg-12 col-sm-12 mb-lg-3 mt-1 mt-3 mt-sm-0 pt-3">
                        <div className="d-flex align-items-center justify-content-between gap-1">
                            <button
                                className={`btn btn-sm col-6 rounded d-flex align-items-center justify-content-center gap-1 reset-button shrinkable-btn ${
                                    isLoggedIn ? '' : 'mb-3'
                                }`}
                                style={{ backgroundColor: '#294672' }}
                                onClick={handleReset}
                                disabled={is_loading}
                            >
                                <FontAwesomeIcon color="#fff" icon={faArrowRotateRight} />
                                <span style={{ color: '#fff' }}>Reset</span>
                            </button>
                            {isLoggedIn && (
                                <button
                                    className="btn btn-sm col-6 rounded d-flex align-items-center justify-content-center gap-1"
                                    style={{ backgroundColor: '#294672' }}
                                    onClick={() => setIsCreatefilterModal((prev) => !prev)}
                                >
                                    <FontAwesomeIcon color="#fff" icon={faFloppyDisk} />
                                    <span className="" style={{ color: '#fff' }}>
                                        Save
                                    </span>
                                </button>
                            )}
                        </div>
                        <div id="searchResults"></div>
                    </div>
                </div>

                {isLoggedIn && (
                    <div className="d-flex flex-column align-items-lg-start px-1 gap-2" style={{ width: '100%' }}>
                        <div className="col-lg-12 col-sm-12 mb-lg-3 mt-1 p-0">
                            <div className="tab-right-details-data savedFilters-container">
                                <div className="accordion-filters-containe mt-2 mt-lg-0 mb-4">
                                    <Accordion
                                        className="rounded"
                                        style={{
                                            padding: 0,
                                            paddingLeft: 1,
                                            paddingRight: 1,
                                            backgroundColor: '#E4ECFA',
                                            margin: 0,
                                            color: '#000',
                                        }}
                                    >
                                        <Accordion.Header onClick={() => setShowSavedFiltersAccordion(!showSavedFiltersAccordion)}>
                                            <span className="fw-bold text-center text-sd">Saved Filters List</span>
                                        </Accordion.Header>
                                        <Accordion.Body className="p-1">
                                            <InfiniteScroll
                                                dataLength={savedSearchFiltersData?.length}
                                                next={fetchMoreData}
                                                hasMore={hasMore}
                                                style={{ maxHeight: 200 }}
                                                className="saved-search-filters rounded"
                                                loader={
                                                    filterDataLoading ? (
                                                        <div className="d-flex align-items-center justify-content-center gap-1">
                                                            {' '}
                                                            <h6>loading... </h6>{' '}
                                                            <FontAwesomeIcon icon={faGear} className="rotate-infinite me-2" />
                                                        </div>
                                                    ) : (
                                                        ''
                                                    )
                                                }
                                                scrollableTarget="scrollableDiv"
                                            >
                                                {!filterDataLoading && !savedSearchFiltersData?.length ? (
                                                    <div
                                                        className="d-flex flex-column align-items-center justify-content-between"
                                                        style={{ height: '-webkit-fill-available' }}
                                                    >
                                                        <span className="d-block text-center text-sm text-secondary">no data</span>
                                                        <p className="font-size-10 fw-medium">
                                                            Note: You can save filter by clicking on Save button above.
                                                        </p>
                                                    </div>
                                                ) : (
                                                    savedSearchFiltersData?.map((item, i) => (
                                                        <div key={i} className="d-flex filter-data-search-page-cus mt-1">
                                                            <div
                                                                className="ps-1 filter-name-list text-truncate d-flex align-items-center cursor-pointer"
                                                                title={item.filter_name}
                                                                onClick={() => handleRedirectSearch(item.search_filter_id)}
                                                            >
                                                                <span className="list-font-cus saved-search-filters-item ">
                                                                    {item.filter_name}
                                                                </span>
                                                            </div>
                                                            <div className="close-filter">
                                                                <DropdownButton
                                                                    id="dropdown-basic-button"
                                                                    className="btn-sm"
                                                                    title={<FontAwesomeIcon icon={faEllipsisVertical} />}
                                                                >
                                                                    <Dropdown.Item
                                                                        onClick={() => {
                                                                            handleRenameFilter(item.filter_name, item.search_filter_id);
                                                                        }}
                                                                    >
                                                                        Rename
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item
                                                                        onClick={() => {
                                                                            handleDeleteFilter(item.search_filter_id);
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </Dropdown.Item>
                                                                </DropdownButton>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </InfiniteScroll>
                                        </Accordion.Body>
                                    </Accordion>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <CreateFilter
                show={isCreatefilterModal}
                filterData={filterObj}
                toggle={() => setIsCreatefilterModal((prev) => !prev)}
                handleFilterListPagination={handleUpdateSavedFilterListAfterCreateFilter}
                {...rest}
            />
            <RenameFilter
                showRename={showRename}
                filterRename={filterRename}
                handleFilterListPagination={handleRenameSavedFilter}
                toggle={() => setShowRename(false)}
                {...rest}
            />
        </div>
    );
};

const mapStatesToProps = ({
    common: {
        countryData,
        currencyData,
        issueTypeData,
        industryData,
        regionData,
        maturityData,
        isCountryLoader = false,
        isIssueTypeLoader,
    },
}: RootState) => {
    return { countryData, isCountryLoader, currencyData, maturityData, issueTypeData, industryData, regionData, isIssueTypeLoader };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
    bindActionCreators(
        {
            ...commonAction,
        },
        dispatch as AppDispatch
    );
const connector = connect(mapStatesToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default memo(connector(SearchForm));
